var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "@mui/material";
//import global css
import "../styles/global.css";
//functions for transforming the data and managing the lsd values
import { getResultColumns } from "../utils/results/resultFunctions";
import Breadcrumb from "../components/Breadcrumb";
import HomeIcon from "@mui/icons-material/Home";
import { Dashboard } from "@mui/icons-material";
// Data Access
import { getTrial, getTrialResultTable } from "../utils/api/trials/dataAccess";
import LoadingBackdrop from "../components/LoadingBackdrop";
//================================================================================================
// Result Header
//================================================================================================
import ResultHeader from "../components/result/ResultHeader";
import ResultTableContainer from "../components/result/ResultTableContainer";
import { Ptn, } from "../utils/api/result-category/dataAccess";
import { Ptn as PtnResult } from "../utils/api/result/dataAccess";
import ResultGraphStacked from "../components/result/ResultGraphStacked";
//Help about trials modal
// ResultTableHelpModal
import ResultTableHelpModal from "../components/result/ResultTableHelpModal";
//================================================================================================
//                                        RESULTS PAGE
//================================================================================================
const ResultsPage = () => {
    //query param
    const { trialID } = useParams();
    const [graphResults, setGraphResults] = useState([]);
    const [loadingTrial, setLoadingTrial] = useState(true);
    const [loadingResults, setLoadingResults] = useState(true);
    const [currentTrial, setCurrentTrial] = useState();
    const [currentResults, setCurrentResults] = useState([]);
    const [trialResultGroup, setTrialResultGroup] = useState([]);
    const [categoriesFetched, setCategoriesFetched] = useState(false);
    //result categories
    const [distinctResultCategories, setDistinctResultCategories] = useState([]);
    const [currentResultCategoryID, setCurrentResultCategoryID] = useState("");
    const [sortConfig, setSortConfig] = useState(null);
    //handle the Graph Button click to show the graph component and hide the table component
    const [resultColumns, setResultColumns] = useState([]);
    const [showItem, setShowItem] = useState("table");
    const [showHelpModal, setShowHelpModal] = useState(false);
    const navigate = useNavigate();
    const handleGraphButtonClick = () => {
        setShowItem("graph");
    };
    const handleTableButtonClick = () => {
        setShowItem("table");
    };
    const handleHelpButtonClick = () => {
        setShowHelpModal(!showHelpModal);
    };
    // state for the data type checkboxes bubbled up from the ResultDataTypeCheckboxList component
    const [checkedDataTypeIds, setCheckedDataTypeIds] = useState([]);
    //================================================================================================
    //                           #1 Trial Data for the headers
    // e.g. Trial Name, Trial Year, Trial Location, etc.
    //================================================================================================
    useEffect(() => {
        if (!trialID) {
            setLoadingTrial(false);
            return;
        }
        setLoadingTrial(true);
        const load = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                const trialResponse = yield getTrial(trialID);
                if (trialResponse.status === 200 && trialResponse.data.length > 0) {
                    ///=================================================================================================
                    //                    graph data
                    //=====================================================================================
                    // for the jsonData we need the categoryId for "Seasonal"
                    let seasonalCategoryId = (_a = distinctResultCategories.find((category) => category.categoryName === "Seasonal")) === null || _a === void 0 ? void 0 : _a.id;
                    //while we are here, set the resultColumns 
                    const jsonData = yield getTrialResultTable(trialID, seasonalCategoryId !== null && seasonalCategoryId !== void 0 ? seasonalCategoryId : "");
                    if (jsonData && jsonData.data.length > 0) {
                        console.log("Result Columns JSON: ", jsonData.data);
                        const columns = getResultColumns(jsonData.data);
                        setResultColumns(columns);
                    }
                    else {
                        setResultColumns([]);
                    }
                    //console.log("Result Columns: ", resultColumns);
                    setCurrentTrial(trialResponse.data[0]);
                }
                else {
                    setCurrentTrial(null);
                }
            }
            catch (err) {
                console.error(err);
            }
            finally {
                setLoadingTrial(false);
            }
        });
        const handle = setTimeout(() => {
            load();
        }, 100);
        return () => clearTimeout(handle);
    }, [trialID]);
    //================================================================================================
    //                                #2 DISTINCT RESULT CATEGORIES
    //================================================================================================
    useEffect(() => {
        const fetchResultCategories = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            if (!(currentTrial === null || currentTrial === void 0 ? void 0 : currentTrial.id))
                return;
            try {
                // /console.log("Fetching Result Categories for trial: ", currentTrial.id);
                const categories = yield Ptn.ResultCategory.GetResultCategories(currentTrial.id);
                if (categories.length === 0)
                    return;
                //console.log("Result Categories: ", categories);
                setDistinctResultCategories(categories);
                const defaultCategory = (_a = categories.find((category) => category.categoryName === "Seasonal")) !== null && _a !== void 0 ? _a : (categories.length > 0 ? categories[0] : null);
                setCurrentResultCategoryID((_b = defaultCategory === null || defaultCategory === void 0 ? void 0 : defaultCategory.id) !== null && _b !== void 0 ? _b : ''); // Set the first category as the default selected category
                setCategoriesFetched(true); // Mark categories as fetched
            }
            catch (error) {
                console.error("Error fetching result categories:", error);
            }
        });
        fetchResultCategories();
    }, [currentTrial]);
    //================================================================================================
    // Function to iterate through the distinct Categories for a trial and call the GetTrialResults. 
    // Then, add this to the trialResultGroup state if the category is not already present.
    //================================================================================================
    const getTrialResults = useCallback((trialId) => __awaiter(void 0, void 0, void 0, function* () {
        const resultGroups = [...trialResultGroup]; // Copy the existing state
        let hasNewResults = false;
        //console.log("Getting Trial Results for: ", trialId, distinctResultCategories);
        for (const category of distinctResultCategories) {
            // Check if the trial and category already exist in the resultGroups
            const exists = resultGroups.some((group) => group.trialId === trialId && group.categoryId === category.id);
            if (!exists) {
                const results = yield PtnResult.ResultTableRow.GetTrialResults(trialId, category.id);
                //console.log("Results in Results.tsx:getTrialResults: ", results, trialId, category.id);
                if (results.length > 0) {
                    resultGroups.push({
                        trialId: trialId,
                        categoryId: category.id,
                        trialResults: results,
                    });
                    hasNewResults = true;
                }
            }
        }
        if (hasNewResults) {
            setTrialResultGroup(resultGroups); // Update the state only if new results are added
        }
        if (!currentResultCategoryID || currentResultCategoryID === "") {
            console.log("--------------- 1Setting Current Results to: ", trialResultGroup);
            setCurrentResults(resultGroups[0].trialResults);
        }
        else {
            const currentCategoryResultGroup = resultGroups.find((group) => group.categoryId === currentResultCategoryID);
            if (currentCategoryResultGroup) {
                console.log("---------------Setting Current Results to: ", currentCategoryResultGroup.trialResults);
                setCurrentResults(currentCategoryResultGroup.trialResults);
            }
            else {
                setCurrentResults([]); // Reset to empty if no results found for the current category
            }
        }
        setLoadingResults(false); // Set loading to false after fetching results
    }), [distinctResultCategories, trialResultGroup, currentResultCategoryID]);
    //================================================================================================
    //                                POPULATION OF TABLE DATA
    //================================================================================================
    useEffect(() => {
        if (!trialID || loadingResults || loadingTrial) {
            return;
        }
        if (resultColumns.length === 0 ||
            !Array.isArray(currentResults) ||
            currentResults.length === 0) {
            navigate("/?error=No data found for this trial");
            return;
        }
        if (!currentResultCategoryID || currentResultCategoryID === "") {
            const category = currentResults[0].categoryID;
            if (category) {
                setCurrentResultCategoryID(category);
            }
        }
        else {
            console.log("Current Result Category ID: ", currentResultCategoryID, "Result columns: ", resultColumns);
        }
        const resultCells = resultColumns.reduce((acc, column) => {
            // changed to lower case to avoid case sensitivity issues
            if (column.name.toLowerCase().includes("total") || column.name.toLowerCase() === "persistence") {
                return acc; // Skip columns with "total" in their name or named "persistence"
            }
            return acc.concat(column.columnCells);
        }, []);
        //sort the cells by rowIndex and columnIndex
        resultCells.sort((a, b) => {
            var _a, _b;
            if (a.rowIndex === b.rowIndex) {
                return a.columnIndex - b.columnIndex;
            }
            return ((_a = a.cellNumericValue) !== null && _a !== void 0 ? _a : 0) - ((_b = b.cellNumericValue) !== null && _b !== void 0 ? _b : 0); //a.RowIndex - b.RowIndex;
        });
        console.log("Result Cells: ", resultCells);
        setGraphResults(resultCells);
    }, [
        trialID,
        currentResultCategoryID,
        navigate,
        resultColumns,
        currentResults,
        loadingResults,
        loadingTrial,
    ]);
    //================================================================================================
    //                                  SORTING
    //================================================================================================
    const handleColumnClick = (columnIndex, columnID) => {
        let direction = "desc";
        if (sortConfig &&
            sortConfig.selectedColumnIndex === columnIndex &&
            sortConfig.direction === "desc") {
            direction = "asc";
        }
        setSortConfig({
            selectedColumnIndex: columnIndex,
            direction,
            selectedColumnID: columnID,
        });
        // other components will update based on this.
        //console.log("Sort Config: ", columnID, columnIndex, direction);
    };
    //================================================================================================
    //                              RESULT CATEGORY ID CHANGE
    // When the user selects a new result category, the table will update with the new data
    //================================================================================================
    const handleResultCategoryChange = (categoryId, categoryName) => __awaiter(void 0, void 0, void 0, function* () {
        setCurrentResultCategoryID(categoryId);
    });
    useEffect(() => {
        if (!trialID)
            return;
        // set the current selected column to "";
        if (currentResultCategoryID === "Quality") {
            setSortConfig({
                selectedColumnIndex: 1,
                direction: "asc",
                selectedColumnID: "",
            });
        }
        if (currentResultCategoryID !== "Quality") {
            setCheckedDataTypeIds([]);
        }
        //set sortConfig
        setSortConfig({
            selectedColumnIndex: 1,
            direction: "asc",
            selectedColumnID: "",
        });
    }, [currentResultCategoryID, trialID]);
    //================================================================================================
    //                                GET TRIAL RESULTS
    useEffect(() => {
        if (categoriesFetched && (currentTrial === null || currentTrial === void 0 ? void 0 : currentTrial.id) && distinctResultCategories.length > 0) {
            // console.log(
            //   "Fetching Trial Results for trial: ",
            //   currentTrial.id,
            //   "as distinctResultCategories are populated"
            // );
            getTrialResults(currentTrial.id);
        }
    }, [categoriesFetched, currentTrial, distinctResultCategories, getTrialResults]);
    //================================================================================================
    //                                Data Type Changes
    //================================================================================================
    const handleDataTypeCheckboxesChanged = (checkedDataTypeIds) => {
        setCheckedDataTypeIds(checkedDataTypeIds);
    };
    //================================================================================================
    //                                        LOADING
    //================================================================================================
    if (loadingTrial || loadingResults) {
        //material ui loading spinner
        //center the spinner vertically and horizontally
        return React.createElement(LoadingBackdrop, { loading: true });
    }
    const breadCrumbItems = [
        { title: "Home", href: "/", icon: React.createElement(HomeIcon, null) },
        { title: "Results", href: "/results", icon: React.createElement(Dashboard, null) },
    ];
    return (React.createElement(Container, { sx: {
            //using windowWidth to determine the width of the container
            // width: isMobile ? windowWidth - 100 : "100%",
            // margin: 0,
            padding: 1,
        } },
        React.createElement(Breadcrumb, { path: "results", breadCrumbItems: breadCrumbItems }),
        React.createElement(ResultHeader, { onTableClick: handleTableButtonClick, onGraphClick: handleGraphButtonClick, onHelpClick: handleHelpButtonClick, onResultCategoryClick: handleResultCategoryChange, onDataTypeCheckboxesChanged: handleDataTypeCheckboxesChanged, currentTrial: currentTrial, distinctResultCategories: distinctResultCategories, selectedResultCategoryId: currentResultCategoryID }),
        showItem === "table" ? (React.createElement(ResultTableContainer, { trialResults: currentResults, distinctColumns: [], headerRowSortConfig: sortConfig !== null && sortConfig !== void 0 ? sortConfig : {
                selectedColumnIndex: 1,
                direction: "asc",
                selectedColumnID: "",
            }, resultRowSortConfig: sortConfig !== null && sortConfig !== void 0 ? sortConfig : {
                selectedColumnIndex: 1,
                direction: "desc",
                selectedColumnID: "",
            }, handleColumnClick: handleColumnClick, trialId: trialID !== null && trialID !== void 0 ? trialID : "", categoryId: currentResultCategoryID !== null && currentResultCategoryID !== void 0 ? currentResultCategoryID : "", checkedDataTypeIds: checkedDataTypeIds })) : (React.createElement(ResultGraphStacked, { data: graphResults, datasetLabels: [""] })),
        React.createElement(ResultTableHelpModal, { open: showHelpModal, onClose: () => setShowHelpModal(false) })));
};
export default ResultsPage;
